import { isNumber } from 'utils/NumberUtils';
import { useReducer, useMemo, useEffect } from 'react';
import { IPoint } from 'utils/GeometryUtils';
import { isLeftButtonPressed } from 'utils/KeyBoardUtils';
import { useDidUpdateEffect } from './useDidUpdateEffect';

export interface IDragAndDropState {
  /**
   * Indicates whether dnd is active.
   * To active dnd need to call "start" method.
   * To stop dnd need to call "stop" method.
   */
  isActive: boolean;
  /**
   * Initial cursor position in window coordinate system.
   */
  initialCursorPoint: IPoint;
  /**
   * Current cursor position in window coordinate system.
   */
  currentCursorPoint: IPoint;
  /**
   * Initial point of your coordinate system which will be editing when calling "move" method.
   * It can be different from "initialCursorPoint" if "initialPoint" is provide in [IDndStartEvent].
   * initialCursorPoint - point of window coordinate system.
   * initialPoint - point of your coordinate system.
   */
  initialPoint: IPoint;
  /**
   * Current point of your coordinate system which has been editing by calling "move" method
   */
  currentPoint: IPoint;
}

export interface IBounds {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
}

export interface IDragAndDropProps {
  /**
   * Determines whether need to log resize events like 'start', 'move', 'stop'
   * @default false
   */
  logEvents?: boolean;
  bounds?: IBounds;
  onDragStart?: (result: IDragAndDropResult) => void;
  onDragging?: (result: IDragAndDropResult) => void;
  onDragEnd?: (result: IDragAndDropResult) => void;
}

export interface IDndEvent {
  cursor: IPoint;
}

export interface IDndStartEvent extends IDndEvent {
  /**
   * Point of your coordinate system which will be editing when calling "move" method.
   * TODO alex: need to provide "converter" method to "useDragAndDrop" instead of providing "initialPoint"
   *
   * @default cursor
   */
  initialPoint?: IPoint;
}

export interface IDragAndDropResult {
  isActive: boolean;
  startPoint: IPoint;
  endPoint: IPoint;
  start: (event: IDndStartEvent) => void;
  stop: (event: IDndEvent) => void;
  move: (event: IDndEvent) => void;
}

function getEndPoint(startPoint: IPoint, startCursorPoint: IPoint, endCursorPoint: IPoint, bounds?: IBounds): IPoint {
  const deltaX = endCursorPoint.x - startCursorPoint.x;
  const deltaY = endCursorPoint.y - startCursorPoint.y;

  let x = startPoint.x + deltaX;
  let y = startPoint.y + deltaY;

  // Keep x and y below right and bottom limits...
  if (isNumber(bounds?.right)) {
    x = Math.min(x, bounds!.right!);
  }
  if (isNumber(bounds?.bottom)) {
    y = Math.min(y, bounds!.bottom!);
  }

  // But above left and top limits.
  if (isNumber(bounds?.left)) {
    x = Math.max(x, bounds!.left!);
  }
  if (isNumber(bounds?.top)) {
    y = Math.max(y, bounds!.top!);
  }

  return {
    x,
    y,
  };
}

/**
 * Creates dnd even from any mouse event.
 * It will prevent copy/pasting this logic.
 */
export function createDndEvent<T extends IDndEvent>(
  e: React.MouseEvent<Element> | MouseEvent,
  otherProps?: Omit<T, 'cursor'>
): T {
  return {
    ...(otherProps ?? {}),
    cursor: {
      x: e.pageX,
      y: e.pageY,
    },
  } as T;
}

export type IDragAndDropAction =
  | { type: 'move' | 'stop'; cursorPosition: IPoint; bounds?: IBounds }
  | { type: 'start'; cursorPosition: IPoint; startPoint: IPoint };

export function dragAndDropReducer(state: IDragAndDropState, action: IDragAndDropAction): IDragAndDropState {
  switch (action.type) {
    case 'start':
      return {
        isActive: true,
        initialCursorPoint: action.cursorPosition,
        currentCursorPoint: action.cursorPosition,
        initialPoint: action.startPoint,
        currentPoint: action.startPoint,
      };
    case 'move':
      if (!state.isActive) {
        return state;
      }

      return {
        ...state,
        ...{
          currentCursorPoint: action.cursorPosition,
          currentPoint: getEndPoint(
            state.initialPoint,
            state.initialCursorPoint,
            state.currentCursorPoint,
            action.bounds
          ),
        },
      };
    case 'stop':
      if (!state.isActive) {
        return state;
      }

      return {
        ...state,
        isActive: false,
        currentCursorPoint: action.cursorPosition,
        currentPoint: getEndPoint(
          state.initialPoint,
          state.initialCursorPoint,
          state.currentCursorPoint,
          action.bounds
        ),
      };
  }
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function useDragAndDrop(props?: IDragAndDropProps): IDragAndDropResult {
  const [{ isActive, initialPoint: startPoint, currentPoint: endPoint }, dispatch] = useReducer(dragAndDropReducer, {
    isActive: false,
    initialCursorPoint: { x: 0, y: 0 },
    currentCursorPoint: { x: 0, y: 0 },
    initialPoint: { x: 0, y: 0 },
    currentPoint: { x: 0, y: 0 },
  });

  const result = useMemo(
    () => ({
      isActive,
      startPoint,
      endPoint,
      start: (e: IDndStartEvent) => {
        const action: IDragAndDropAction = {
          type: 'start',
          cursorPosition: e.cursor,
          startPoint: e.initialPoint ?? e.cursor,
        };
        props && props.logEvents && console.log(action);
        dispatch(action);
      },
      move: (e: IDndEvent) => {
        if (!isActive) {
          return;
        }

        const action: IDragAndDropAction = {
          type: 'move',
          cursorPosition: e.cursor,
          bounds: props?.bounds,
        };
        props && props.logEvents && console.log(action);
        dispatch(action);
      },
      stop: (e: IDndEvent) => {
        if (!isActive) {
          return;
        }

        const action: IDragAndDropAction = { type: 'stop', cursorPosition: e.cursor, bounds: props?.bounds };
        props && props.logEvents && console.log(action);
        dispatch(action);
      },
    }),
    [endPoint, isActive, props, startPoint]
  );

  useDidUpdateEffect(() => {
    if (isActive) {
      props?.onDragStart?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, props]);

  useDidUpdateEffect(() => {
    if (isActive) {
      props?.onDragging?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, props]);

  useDidUpdateEffect(() => {
    if (!isActive) {
      props?.onDragEnd?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endPoint, isActive]);

  useEffect(() => {
    function stop(event: MouseEvent) {
      result.stop(createDndEvent(event));
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
    }

    const mouseMove = (event: MouseEvent) => {
      // sometimes for some reasone, button can be unpressed during "mousemove", but "moseup" event doesn't happen
      // so we check whether button is pressed and call "stop" if it doesn't.
      if (isLeftButtonPressed(event)) {
        result.move(createDndEvent(event));
      } else {
        stop(event);
      }
    };

    const mouseUp = (event: MouseEvent) => {
      stop(event);
    };

    document.addEventListener('mousemove', mouseMove);
    document.addEventListener('mouseup', mouseUp);

    return () => {
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
    };
  }, [result]);

  return result;
}
